import { InputField } from '@shared/InputField'
import IMask from 'imask/holder'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd'

import { InfoCircleOutlined } from '@ant-design/icons'

import Dialog from '@shared/Dialog'
import SelectGlobal from '@shared/Select'
import { ModalProvider } from '@shared/Modal'
import PersenolIdInfo from '@shared/TrustedPerson/PersenolIdInfo'
import {
  MobilOperatorType,
  OrderBusinessCardType,
  useCardHolderByPinMutation,
} from '@app/store/reducers/salaryCards/businessCardOrder.api'
import { usePhoneFormatter } from '@shared/hooks/usePhoneFormatter'
import { useFetchCheckDirectorWithoutAsanMutation } from '@app/store/reducers/ibsigning/checkDirector'

function UserInfoForm() {
  const { t } = useTranslation()
  const formatPhone = usePhoneFormatter()

  const { control, setValue, trigger } = useFormContext<OrderBusinessCardType>()

  const serialType = useWatch({
    name: 'cardHolder.serialType',
    control,
  })

  const isMe = useWatch({
    name: 'cardHolder.isMe',
    control,
  })

  const [setByMe, { isSuccess, isLoading, reset }] =
    useCardHolderByPinMutation({ fixedCacheKey: 'CardHolderByPin' })

  const [checkDirector, { isSuccess: isSuccessDr }] =
    useFetchCheckDirectorWithoutAsanMutation()

  const setByMeFn = async (isMe: boolean) => {
    if (isMe) {
      try {
        const data = await setByMe().unwrap()
        const { formatted } = formatPhone(data?.phoneNumber)

        setValue('cardHolder.isDocExpired', !data?.isDocExpired, {
          shouldValidate: true,
        })
        await trigger('frontFileId');
        
        setValue('cardHolder.cardHolderFullName', data?.customerInfo, {
          shouldValidate: true,
        })
        setValue('cardHolder.pin', data?.pin, { shouldValidate: true })
        setValue('cardHolder.phoneNumber', formatted, {
          shouldValidate: true,
        })
        setValue('cardHolder.serialType', data?.serialType, {
          shouldValidate: true,
        })
        setValue('cardHolder.serialNo', data?.serialNo, {
          shouldValidate: true,
        })

      } catch (error) {
        setValue('cardHolder.isMe', false, { shouldValidate: true })

        Dialog.warning({
          title: t('common.attention'),
          modalType: 'failed',
          message: error?.data,
          okText: t('orders.salaryCardOrder.buttonsLabels.close'),
        })
      }
    } else {
      reset()
      setValue('cardHolder.isMe', false, { shouldValidate: true })
      setValue('cardHolder.isDocExpired', false, { shouldValidate: true })
      setValue('cardHolder.cardHolderFullName', '', { shouldValidate: true })
      setValue('cardHolder.pin', '', { shouldValidate: true })
      setValue('cardHolder.phoneNumber', '', { shouldValidate: true })
      setValue('cardHolder.serialNo', '', { shouldValidate: true })
      setValue('cardHolder.serialType', 'AZE', { shouldValidate: true })
      setValue('cardHolder.mobileOperatorName', null, { shouldValidate: true })

      await trigger('frontFileId');
    }
  }

  useEffect(() => {
    checkDirector({ docType: 'BCO' })
  }, [])

  return (
    <Row
      style={{
        boxSizing: 'border-box',
        backgroundColor: '#F3F6FA',
        borderRadius: 16,
        padding: 10,
        flexDirection: 'column',
      }}
      gutter={[0, 20]}
    >
      <Col span={24}>
        <Row gutter={[0, 20]} align="middle" justify="space-between">
          <Space>
            <Typography.Text style={{ fontSize: 18 }} strong>
              {t('businessCardOrders.labels.cardHolderInfoTitle')}
            </Typography.Text>
            <Tooltip
              title={t('businessCardOrders.cardHolderInfoI')}
            >
              <InfoCircleOutlined
                style={{ fontSize: 16, cursor: 'pointer', color: '#015CD1' }}
              />
            </Tooltip>
          </Space>
          <Controller
            name="cardHolder.isMe"
            disabled={!isSuccessDr || isLoading}
            control={control}
            render={({ field: { value, name, onChange, disabled } }) => (
              <Checkbox
                disabled={disabled}
                name={name}
                value={value}
                checked={value}
                onChange={e => {
                  setByMeFn(e.target.checked)
                  onChange(e)
                }}
                style={{
                  backgroundColor: '#E7F0F9',
                  padding: 10,
                  borderRadius: 8,
                }}
              >
                {t('businessCardOrders.my')}
              </Checkbox>
            )}
          />
        </Row>
      </Col>

      <Col span={24}>
        <Controller
          name="cardHolder.cardHolderFullName"
          control={control}
          render={({ field, fieldState: { error, invalid } }) => (
            <InputField
              label={t('orders.salaryCardOrder.inputLabels.fullName')}
              placeholder={t('orders.fullNamePlace')}
              labelCol={{ span: 4 }}
              disabled={isSuccess || isLoading || isMe}
              wrapperCol={{ span: 20 }}
              style={{ margin: 0 }}
              maxLength={200}
              validateStatus={invalid ? 'error' : 'success'}
              help={invalid ? error?.message : ''}
              {...field}
            />
          )}
        />
      </Col>

      <Col span={24}>
        <Row gutter={12}>
          <Col span={12}>
            <Controller
              name="cardHolder.pin"
              control={control}
              render={({ field, fieldState: { error, invalid } }) => (
                <InputField
                  disabled={isSuccess || isLoading || isMe}
                  label={
                    <>
                      {t('orders.salaryCardOrder.inputLabels.pin')}
                      <ModalProvider
                        title={t('common.attention')}
                        centered
                        width={900}
                        modalContent={<PersenolIdInfo />}
                      >
                        {({ openModal }) => (
                          <InfoCircleOutlined
                            onClick={() => openModal('')}
                            style={{
                              fontSize: 14,
                              marginLeft: '4px',
                              cursor: 'pointer',
                              color: '#015CD1',
                            }}
                          />
                        )}
                      </ModalProvider>
                    </>
                  }
                  labelCol={{ span: 8 }}
                  placeholder={t('orders.enters')}
                  style={{ margin: 0 }}
                  mask={IMask.MaskedPattern}
                  maskOptions={{
                    mask: /^[A-Za-z0-9]{0,7}$/,
                    prepare: (str) => str.toUpperCase(),
                  }}
                  validateStatus={invalid ? 'error' : null}
                  help={invalid ? error?.message : ''}
                  // onChange={(value) => debounce(value)}
                  {...field}
                />
              )}
            />
          </Col>
          <Row>
            <Input.Group compact>
              <Col span={14}>
                <Controller
                  name="cardHolder.serialType"
                  control={control}
                  render={({ field: { name, onChange, value, disabled }, fieldState: { error, invalid } }) => (
                    <SelectGlobal
                      disabled={isLoading}
                      style={{ margin: 0 }}
                      label={t('businessCardOrders.labels.serialTypeAndNo')}
                      onSelect={() => trigger('cardHolder.serialNo')}
                      labelCol={{ span: 14 }}
                      wrapperCol={{ span: 10 }}
                      name={name}
                      onChange={onChange}
                      value={value}
                      allowClear={false}
                      options={[
                        { label: 'AZE', value: 'AZE' },
                        { label: 'AA', value: 'AA' },
                        { label: 'AB', value: 'AB' },
                      ]}
                      validateStatus={invalid ? 'error' : undefined}
                      help={invalid ? t(error?.message) : ''}
                    />
                  )}
                />
              </Col>

              <Col span={10}>
                <Controller
                  name="cardHolder.serialNo"
                  control={control}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <InputField
                      {...field}
                      disabled={isLoading}
                      mask={IMask.MaskedRange}
                      maskOptions={{
                        mask: /^[0-9]{0,20}$/,
                        prepare: (str) => str.toUpperCase(),
                      }}
                      placeholder='00000000'
                      maxLength={serialType === 'AA' ? 7 : 8}
                      style={{ margin: 0 }}
                      wrapperCol={{ span: 23 }}
                      validateStatus={invalid ? 'error' : undefined}
                      help={invalid ? t(error?.message) : ''}
                    />
                  )}
                />
              </Col>
            </Input.Group>
          </Row>
        </Row>
      </Col>
      <Col span={24}>
        <Row gutter={12}>
          <Col span={12}>
            <Controller
              name="cardHolder.phoneNumber"
              control={control}
              render={({ field, fieldState: { error, invalid } }) => {
                return (
                  <InputField
                    label={t('orders.salaryCardOrder.inputLabels.mobileNumber')}
                    labelCol={{ span: 8 }}
                    validateStatus={invalid ? 'error' : null}
                    style={{ margin: 0 }}
                    help={invalid ? error?.message : ''}
                    mask={IMask.MaskedPattern}
                    maskOptions={{
                      mask: '(##) 000-00-00',
                      blocks: {
                        '##': {
                          mask: IMask.MaskedEnum,
                          enum: [
                            '50',
                            '51',
                            '55',
                            '70',
                            '77',
                            '90',
                            '99',
                            '10',
                            '11',
                          ],
                        },
                      },
                      definitions: {
                        '0': /[0-9]/,
                      },
                    }}
                    disabled={isLoading}
                    {...field}
                  />
                )
              }}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="cardHolder.mobileOperatorName"
              control={control}
              disabled={isLoading}
              render={({ field: { name, onChange, value, disabled }, fieldState: { error, invalid } }) => {
                return (
                  <Form.Item style={{ margin: 0 }}>
                    <SelectGlobal
                      style={{ margin: 0 }}
                      labelCol={{ span: 10 }}
                      name={name}
                      onChange={onChange}
                      value={value}
                      disabled={disabled}
                      label={
                        <>
                          {t('businessCardOrders.labels.mobilOperator')}
                          <Tooltip title={t('businessCardOrders.mobilOperatorInfo')}>
                            <InfoCircleOutlined
                              style={{
                                fontSize: 14,
                                marginLeft: '4px',
                                cursor: 'pointer',
                                color: '#015CD1',
                              }}
                            />
                          </Tooltip>
                        </>
                      }
                      allowClear={false}
                      placeholder={t('businessCardOrders.labels.select1')}
                      options={Object.entries(MobilOperatorType).map(
                        ([value, label]) => ({
                          value,
                          label,
                        })
                      )}
                      validateStatus={invalid ? 'error' : 'success'}
                      help={invalid ? t(error?.message) : ''}
                    />
                  </Form.Item>
                )
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default UserInfoForm
